import type { CSSProperties } from 'react';
import { cn } from '@clarix/ui';
import type { AppDefinition } from '@clarix/shared-types';

/**
 * The app's identity mark — its icon glyph on a tile in its own brand colour.
 *
 * This is deliberately NOT the bundled favicon art, which the switcher and the
 * launcher both used to render. Every favicon under `public/favicons/` is the
 * SAME abstract Clarix mark (two rounded squares joined by arcs) recoloured per
 * app, so at 20px the apps were told apart only by hue — Projects and CRM are
 * the identical shape in blue and orange. A glyph says what the app IS.
 *
 * The pairing (which glyph, which colour) matches the workspace app shelf
 * exactly — `apps/workspace/src/pages/workspace/app-meta.ts` — so one product
 * reads the same wherever it is listed. Both sides source the colour from the
 * registry's `brandColor`, which is the favicon's dominant tone; keep the two
 * in step when an app's icon or brand colour changes (the workspace remote
 * cannot import this registry — root hard rule 1 — so its copy is a mirror).
 */
export function AppMark({
  app,
  className,
  glyphClassName,
}: {
  app: AppDefinition;
  /** Tile size, e.g. `h-10 w-10`. */
  className?: string;
  glyphClassName?: string;
}) {
  const Icon = app.icon;
  // The brand colour is per-app runtime data, so it rides on a CSS variable
  // rather than a literal class (UI rule 1). Apps with no brandColor fall back
  // to a neutral token tile so a newly-registered app is never blank.
  const tint = { '--app-mark': app.brandColor ?? 'rgb(var(--muted-foreground))' } as CSSProperties;

  return (
    <span
      aria-hidden
      style={tint}
      className={cn('grid shrink-0 place-items-center rounded-xl bg-[var(--app-mark)]', className)}
    >
      <Icon
        className={cn('text-primary-foreground h-1/2 w-1/2', glyphClassName)}
        strokeWidth={1.9}
      />
    </span>
  );
}
